import { useContext, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import TextInput from "./textInput";
import useSubmitForm from "../hooks/useSubmitForm";
import { NavigationContext } from "../context/navigation";
import useButtonTracking from "../hooks/useButtonTracking";

export interface FormValues {
  yourFirstName: string;
  yourLastName: string;
  yourEmail: string;
  subscribeToMarketing: boolean;
  recipientFirstName: string;
  recipientLastName: string;
  recipientEmail: string;
  message?: string;
}

const Form = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();
  const mutation = useSubmitForm();
  const { setActiveRoute } = useContext(NavigationContext);
  const [hintImpression, hintClick] = useButtonTracking({
    text: "Send Hint",
    placement: "right",
    targetpath: "#",
  });
  const { t } = useTranslation();

  const onSubmit = handleSubmit(async (values) => {
    hintClick();
    await mutation.mutateAsync(values);
    setActiveRoute("thanks");
  });

  const patternName = useMemo(() => {
    return {
      required: true,
      pattern: /^[A-Za-z]+$/i,
      minLength: 2,
    };
  }, []);

  const patternEmail = useMemo(() => {
    return {
      required: true,
      pattern: /^\S+@\S+\.\S+$/,
      minLength: 2,
    };
  }, []);

  return (
    <form className="space-y-3 pt-11" onSubmit={onSubmit}>
      <h3 className="text-xl font-bold text-gray-900">{t("About you")}</h3>

      <div className="flex space-x-3">
        <Controller
          name="yourFirstName"
          control={control}
          defaultValue=""
          rules={patternName}
          render={({ field }) => (
            <TextInput
              {...field}
              fullWidth
              label={t("Your first name")}
              color={errors.yourFirstName ? "error" : "primary"}
              variant="filled"
            />
          )}
        />

        <Controller
          name="yourLastName"
          control={control}
          defaultValue=""
          rules={patternName}
          render={({ field }) => (
            <TextInput
              {...field}
              fullWidth
              label={t("Your last name")}
              color={errors.yourLastName ? "error" : "primary"}
              variant="filled"
            />
          )}
        />
      </div>

      <div className="block">
        <Controller
          name="yourEmail"
          control={control}
          defaultValue=""
          rules={patternEmail}
          render={({ field }) => (
            <TextInput
              {...field}
              fullWidth
              label={t("Your email address")}
              color={errors.yourEmail ? "error" : "primary"}
              variant="filled"
            />
          )}
        />
      </div>

      <div className="block">
        <FormControlLabel
          control={
            <Controller
              name="subscribeToMarketing"
              control={control}
              defaultValue={true}
              render={({ field }) => {
                return (
                  <Checkbox
                    checked={field.value}
                    onChange={field.onChange}
                    disableRipple={true}
                    color="default"
                  />
                );
              }}
            />
          }
          label={t(
            "I’d like to become a Bose insider and receive marketing communications."
          )}
          sx={{
            "& .MuiTypography-root": {
              fontFamily: "Gotham, sans-serif",
              fontSize: "12px",
              lineHeight: "16px",
              color: "#1d1d1d",
            },
          }}
        />
      </div>

      <h3 className="text-xl font-bold pt-5 text-gray-900">
        {t("About the recipient")}
      </h3>
      <div className="flex space-x-3">
        <Controller
          name="recipientFirstName"
          control={control}
          defaultValue=""
          rules={patternName}
          render={({ field }) => (
            <TextInput
              {...field}
              fullWidth
              label={t("Recipient's first name")}
              color={errors.recipientFirstName ? "error" : "primary"}
              variant="filled"
            />
          )}
        />
        <Controller
          name="recipientLastName"
          control={control}
          defaultValue=""
          rules={patternName}
          render={({ field }) => (
            <TextInput
              {...field}
              fullWidth
              label={t("Recipient's last name")}
              color={errors.recipientLastName ? "error" : "primary"}
              variant="filled"
            />
          )}
        />
      </div>

      <div className="block">
        <Controller
          name="recipientEmail"
          control={control}
          defaultValue=""
          rules={patternEmail}
          render={({ field }) => (
            <TextInput
              {...field}
              fullWidth
              label={t("Recipient's email address")}
              color={errors.recipientEmail ? "error" : "primary"}
              variant="filled"
            />
          )}
        />
      </div>

      <div className="block">
        <Controller
          name="message"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextInput
              {...field}
              fullWidth
              multiline
              label={t("Personal message")}
              variant="filled"
              rows={3}
            />
          )}
        />
      </div>

      <div className="flex justify-end pt-2">
        <div {...hintImpression}>
          <button className="bg-black text-white uppercase px-7 py-4 rounded-full font-medium tracking-widest text-lg leading-3">
            {t("Send hint")}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Form;
